import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { School } from '../../../types/School';
import { useEffect, useState } from 'react';
import { addSchool, fetchSchools } from '../../../services/schools';
import { getUserSchool, updateUserSchool } from '../../../services/users';
import { useSelector } from 'react-redux';
import { RootState, store } from '../../../store/store';
import { hapticsImpactLight } from '../../../utils/haptics';
import CustomModal from '../CustomModal';
import { Plus } from 'react-feather';
import _ from 'lodash';
import { examCreated } from '../../../slices/exams/examsSlice';
import { schoolReceived } from '../../../slices/school/schoolSlice';
import { getExamLectures } from '../../../services/exams';

// Custom filter function that matches against both school names and domains (acronyms)
const isSchool = (option: SchoolOption): option is School => {
  return option.id !== 'add-school';
};

const filterOptions = (
  options: SchoolOption[],
  { inputValue }: { inputValue: string }
) => {
  const searchTerm = inputValue.toLowerCase();

  return options.filter((option) => {
    if (!isSchool(option)) return true;

    const schoolName = option.name.toLowerCase();
    const domains = option.domains || [];

    const domainMatch = domains.some((domain: string) => {
      const domainWithoutSuffix = domain.split('.')[0].toLowerCase();
      return domainWithoutSuffix.includes(searchTerm);
    });

    const nameMatch = schoolName.includes(searchTerm);

    return nameMatch || domainMatch;
  });
};

type SchoolOption =
  | School
  | { id: 'add-school'; name: string; verified: boolean };

interface SchoolSelectorProps {
  onSelect?: (school: School) => void;
}

const SchoolSelector: React.FC<SchoolSelectorProps> = ({ onSelect }) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const userSchool = useSelector((state: RootState) => state.school.school);

  const [medicalSchools, setMedicalSchools] = useState<School[]>([]);
  const [isAddSchoolModalOpen, setIsAddSchoolModalOpen] = useState(false);
  const [newSchoolName, setNewSchoolName] = useState('');

  const selectedSchoolValue = (() => {
    if (!userSchool || medicalSchools.length === 0) return null;

    // First try to find in the local medicalSchools array
    const foundSchool = medicalSchools.find(
      (school) => school.id === userSchool.id
    );
    if (foundSchool) return foundSchool;

    // If not found in local array but userSchool exists,
    // return userSchool itself (this handles the case when a new school is just added)
    return userSchool;
  })();

  useEffect(() => {
    getSchools();
  }, []);

  useEffect(() => {
    if (
      selectedSchoolValue?.id &&
      selectedSchoolValue.exams &&
      selectedSchoolValue.exams.length > 0
    ) {
      const firstExamId = selectedSchoolValue.exams[0].id;
      if (firstExamId) {
        getExamLectures(firstExamId);
      }
    }
  }, [selectedSchoolValue?.id]);

  const getSchools = async () => {
    try {
      const schools = await fetchSchools();
      if (schools) {
        const verifiedSchools = schools.filter(
          (school) =>
            school.verified ||
            (!school.verified &&
              !_.isNil(currentUser?.schoolId) &&
              currentUser?.schoolId === school.id) ||
            currentUser?.id === school.creatorId
        );
        const sortedSchools = [...verifiedSchools].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setMedicalSchools(sortedSchools);
      }
      if (currentUser?.id) {
        getUserSchool(currentUser.id);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  };

  const handleAddSchool = async () => {
    if (!currentUser?.id) {
      return;
    }
    try {
      const newSchool = await addSchool(newSchoolName, currentUser?.id);
      if (newSchool) {
        // Update local state first
        const updatedSchools = [...medicalSchools, newSchool];
        setMedicalSchools(updatedSchools);

        // Then update Redux state
        optimisticallyUpdateSchoolRedux(newSchool);
      }
      getSchools();
    } catch (error) {
      console.error('Error adding school:', error);
    }
  };

  const optimisticallyUpdateSchoolRedux = (school: School | string) => {
    if (currentUser?.id) {
      // If a school object is passed directly, use it; otherwise find by school ID
      const selectedSchool =
        typeof school === 'string'
          ? medicalSchools.find((s) => s.id === school)
          : school;

      if (selectedSchool) {
        store.dispatch(schoolReceived(selectedSchool));
        const selectedExams = selectedSchool.exams;
        if (selectedExams && selectedExams.length > 0) {
          for (const exam of selectedExams) {
            store.dispatch(examCreated(exam));
          }
        }
      }
    }
  };

  const handleOptionSelect = (option: SchoolOption) => {
    if (option.id === 'add-school') {
      hapticsImpactLight();
      setIsAddSchoolModalOpen(true);
      return;
    }
    if (currentUser?.id) {
      optimisticallyUpdateSchoolRedux(option.id);
      updateUserSchool(currentUser.id, option.id);
      onSelect?.(option);
    }
  };

  return (
    <div className="school-selector">
      <Autocomplete
        id="medical-school-selection"
        key={selectedSchoolValue?.id || 'no-school'}
        options={[
          ...medicalSchools,
          { id: 'add-school', name: 'Add School', verified: true },
        ]}
        filterOptions={filterOptions}
        getOptionLabel={(option: SchoolOption) => option.name}
        value={selectedSchoolValue || undefined}
        disableClearable
        onChange={(_, newSchool) => {
          if (newSchool) {
            handleOptionSelect(newSchool);
          }
        }}
        disabled={!!selectedSchoolValue && currentUser?.ambassador}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={'Select school'}
            style={{ width: '100%', marginTop: '0', fontSize: '16px' }}
          />
        )}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          return option.id === value.id;
        }}
        renderOption={(props, option: SchoolOption) => {
          if (option.id === 'add-school') {
            return (
              <li {...props} key="add-school" className="add-school-option">
                <button
                  className="add-school-option_button"
                  onClick={(e) => {
                    e.stopPropagation();
                    hapticsImpactLight();
                    setIsAddSchoolModalOpen(true);
                  }}
                >
                  <Plus />
                  Add School
                </button>
              </li>
            );
          }
          return <li {...props}>{option.name}</li>;
        }}
      />
      <CustomModal
        open={isAddSchoolModalOpen}
        onClose={() => setIsAddSchoolModalOpen(false)}
        name={'add-school'}
        glass={true}
      >
        <div className="modal_header">
          <h2>Add School</h2>
        </div>
        <div className="modal_content">
          <TextField
            label="School Name"
            value={newSchoolName}
            onChange={(e) => setNewSchoolName(e.target.value)}
          />
        </div>
        <div className="modal_actions">
          <button
            className="button button--secondary"
            onClick={() => {
              hapticsImpactLight();
              setIsAddSchoolModalOpen(false);
            }}
            tabIndex={0}
            aria-label="Cancel"
          >
            Cancel
          </button>
          <button
            className="button"
            onClick={() => {
              hapticsImpactLight();
              handleAddSchool();
              setIsAddSchoolModalOpen(false);
              setNewSchoolName('');
            }}
            disabled={!newSchoolName}
            tabIndex={0}
            aria-label="Add School"
          >
            Add School
          </button>
        </div>
      </CustomModal>
    </div>
  );
};

export default SchoolSelector;
